import { camelize } from 'humps';
import type {
  ChargeClosureStatus, TaxDocumentFailureReason, TaxDocumentKind, TaxDocumentStatus,
} from '@/api/charge-closures';
import type { ChargeTaxDocumentStatus } from '@/api/charges';

type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface StatusChip {
  label: string;
  color: string;
}

/**
 * The one state a user reads for a closure. The closure and its document run two
 * state machines, but between them they answer a single question — how far did
 * this period get towards being billed — and the second machine adds exactly one
 * bit the first does not: whether the turnos are claimed or back in the pool.
 *
 * The document read here is the one the serializer picks (live first, then the
 * newest), and `GenerateShiftsChargesJob#tax_document_status` maps that same
 * document the same way, so the Cobros row and this list never disagree about
 * the same closure.
 */
export type ClosureState = 'voided' | 'emitting' | 'emitted' | 'rejected' | 'failed' | 'unemitted';

export const CLOSURE_STATES: ClosureState[] = [
  'unemitted', 'emitting', 'emitted', 'rejected', 'failed', 'voided',
];

const CLOSURE_STATE_COLORS: Record<ClosureState, string> = {
  unemitted: 'grey',
  emitting: 'primary',
  emitted: 'success',
  rejected: 'error',
  failed: 'error',
  voided: 'grey',
};

const DOCUMENT_STATE: Record<TaxDocumentStatus, ClosureState> = {
  draft: 'emitting',
  submitted: 'emitting',
  accepted: 'emitted',
  rejected: 'rejected',
  failed: 'failed',
  // The credit-note path left nothing live behind it.
  cancelled: 'unemitted',
};

interface StatefulClosure {
  status: ChargeClosureStatus;
  taxDocument: { status: TaxDocumentStatus } | null;
}

export function closureState(closure: StatefulClosure): ClosureState {
  if (closure.status === 'voided') return 'voided';

  return closure.taxDocument ? DOCUMENT_STATE[closure.taxDocument.status] : 'unemitted';
}

export function closureStateChip(closure: StatefulClosure, t: Translate): StatusChip {
  const state = closureState(closure);

  return { label: t(`chargeClosures.state.${state}`), color: CLOSURE_STATE_COLORS[state] };
}

/** The shape both the list row and the detail dialog read a problem from. */
interface ProblemDocument {
  status: TaxDocumentStatus;
  failureReason: TaxDocumentFailureReason;
}

// A document in one of these never produced anything usable, whatever the
// provider said about it.
const PROBLEM_STATUSES: TaxDocumentStatus[] = ['failed', 'rejected'];

/**
 * The sentence a user reads when an emission produced no usable DTE: our own
 * copy, naming the next step. The provider's own words never reach the browser —
 * they stay on the row for support, who read them in the admin.
 *
 * Documents written before `failure_reason` existed carry none, so a failed or
 * rejected one without a reason still says something rather than nothing.
 */
export function taxDocumentProblem(document: ProblemDocument, t: Translate): string {
  const reason = document.failureReason;
  if (reason) return t(`chargeClosures.failureReason.${camelize(reason)}`);
  if (PROBLEM_STATUSES.includes(document.status)) {
    return t('chargeClosures.failureReason.unknown');
  }

  return '';
}

const CHARGE_TAX_DOCUMENT_COLORS = {
  emitting: 'primary',
  emitted: 'success',
  // A DTE went out, but the period has gained turnos since: the closures and
  // the charges differ, and emitting again bills the difference.
  partial: 'warning',
  failed: 'error',
  // A closure froze the turnos but no DTE went out: it needs the user as much
  // as a failed send does.
  unemitted: 'error',
} as const;

type ButtonVariant = 'elevated' | 'text';
interface ChargeTaxDocumentButtonProps {
  color?: string;
  variant: 'tonal' | ButtonVariant;
  elevation?: number;
}

// What Vuetify's own `elevated` variant raises a button by, so a tinted button
// sits at the same height as the plain ones beside it.
const RAISED_ELEVATION = 2;

/**
 * How the emit action on a Cobros row paints itself: blue while the factura is
 * with the provider, green once the authority took it, yellow when the period
 * gained turnos after it, red when a send needs the user. Tonal so the fill
 * matches the money chips rather than shouting, and raised wherever the row's
 * other actions are raised.
 * With nothing to show the button keeps its surface's own look, so pass the
 * variant that surface uses when it is not carrying a state.
 */
export function chargeTaxDocumentButtonProps(
  status: ChargeTaxDocumentStatus, restingVariant: ButtonVariant = 'elevated',
): ChargeTaxDocumentButtonProps {
  if (!status) return { variant: restingVariant };

  const tinted: ChargeTaxDocumentButtonProps = {
    color: CHARGE_TAX_DOCUMENT_COLORS[status], variant: 'tonal',
  };
  if (restingVariant === 'text') return tinted;

  return { ...tinted, elevation: RAISED_ELEVATION };
}

/**
 * What that same action says on hover: the period's emission state when it has
 * one, and plain "Emitir DTE" when there is nothing to report.
 */
export function chargeTaxDocumentLabel(status: ChargeTaxDocumentStatus, t: Translate): string {
  return t(`chargeClosures.emitAction.${status ?? 'idle'}`);
}

/** "Factura electrónica 33"-style label: the kind is universal, the code is the country's. */
export function documentKindLabel(kind: TaxDocumentKind, code: string | null, t: Translate): string {
  const name = t(`chargeClosures.kinds.${camelize(kind)}`);

  return code ? `${name} ${code}` : name;
}

const DEFAULT_SUBUNITS_PER_UNIT = 100;
const SUBUNITS_PER_UNIT: Record<string, number> = { CLP: 1 };

/** Money on the wire is in minor units; CLP has none, so cents are pesos. */
export function centsToAmount(cents: number, currency: string): number {
  return cents / (SUBUNITS_PER_UNIT[currency] ?? DEFAULT_SUBUNITS_PER_UNIT);
}
